/**
 * RPNStep - Step 4: Risk assessment with S, O, D sliders
 */

import React from 'react';
import type { ScratchFormData } from '../ScratchDetailsScreen';
import './RPNStep.css';

interface RPNStepProps {
  data: ScratchFormData;
  onChange: (updates: Partial<ScratchFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export function RPNStep({ data, onChange, onNext, onBack }: RPNStepProps) {
  const handleChange = (field: keyof typeof data.rpn, value: number) => {
    onChange({
      rpn: {
        ...data.rpn,
        [field]: value,
      },
    });
  };

  const calculatedRPN = data.rpn.severity * data.rpn.occurrence * data.rpn.detection;

  const getRiskLevel = (rpn: number) => {
    if (rpn > 200) return { label: 'Critical', color: '#dc2626' };
    if (rpn > 120) return { label: 'High', color: '#ea580c' };
    if (rpn > 40) return { label: 'Medium', color: '#eab308' };
    return { label: 'Low', color: '#10b981' };
  };

  const riskLevel = getRiskLevel(calculatedRPN);

  return (
    <div className="step-container" data-testid="rpn-step">
      <div>
        <h2 className="step-title">Assess the risk</h2>
        <p className="step-subtitle">
          Rate the severity, likelihood, and detectability of this failure. The Risk Priority Number
          (RPN) helps prioritize which failures to address first.
        </p>
      </div>

      <div className="step-form">
        {/* Severity */}
        <div className="form-field">
          <label htmlFor="severity-slider">Severity: {data.rpn.severity}</label>
          <input
            id="severity-slider"
            type="range"
            min="1"
            max="10"
            value={data.rpn.severity}
            onChange={(e) => handleChange('severity', parseInt(e.target.value))}
            data-testid="severity-slider"
            className="rpn-slider"
          />
          <div className="slider-labels">
            <span>1 - Minor</span>
            <span>5 - Moderate</span>
            <span>10 - Catastrophic</span>
          </div>
          <span className="field-hint">How severe would the impact be if this failure occurs?</span>
        </div>

        {/* Occurrence */}
        <div className="form-field">
          <label htmlFor="occurrence-slider">Occurrence: {data.rpn.occurrence}</label>
          <input
            id="occurrence-slider"
            type="range"
            min="1"
            max="10"
            value={data.rpn.occurrence}
            onChange={(e) => handleChange('occurrence', parseInt(e.target.value))}
            data-testid="occurrence-slider"
            className="rpn-slider"
          />
          <div className="slider-labels">
            <span>1 - Rare</span>
            <span>5 - Occasional</span>
            <span>10 - Frequent</span>
          </div>
          <span className="field-hint">How often is this failure likely to occur?</span>
        </div>

        {/* Detection */}
        <div className="form-field">
          <label htmlFor="detection-slider">Detection: {data.rpn.detection}</label>
          <input
            id="detection-slider"
            type="range"
            min="1"
            max="10"
            value={data.rpn.detection}
            onChange={(e) => handleChange('detection', parseInt(e.target.value))}
            data-testid="detection-slider"
            className="rpn-slider"
          />
          <div className="slider-labels">
            <span>1 - Almost Certain</span>
            <span>5 - Moderate</span>
            <span>10 - Cannot Detect</span>
          </div>
          <span className="field-hint">
            How difficult is it to detect this failure before it causes harm?
          </span>
        </div>

        {/* Calculated RPN */}
        <div className="rpn-result" data-testid="rpn-result">
          <div className="rpn-formula">
            RPN = {data.rpn.severity} × {data.rpn.occurrence} × {data.rpn.detection}
          </div>
          <div className="rpn-value" style={{ color: riskLevel.color }} data-testid="rpn-value">
            {calculatedRPN}
          </div>
          <div className="rpn-level" style={{ color: riskLevel.color }}>
            {riskLevel.label} Risk
          </div>
          <div className="rpn-explanation">
            {riskLevel.label === 'Critical' && (
              <span>⚠️ This failure requires immediate attention and mitigation</span>
            )}
            {riskLevel.label === 'High' && (
              <span>⚡ This failure should be prioritized for mitigation</span>
            )}
            {riskLevel.label === 'Medium' && (
              <span>📋 Monitor this failure and consider mitigation if feasible</span>
            )}
            {riskLevel.label === 'Low' && (
              <span>✓ This failure is low priority but should be documented</span>
            )}
          </div>
        </div>
      </div>

      <div className="step-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onBack}
          data-testid="back-button"
        >
          Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onNext}
          data-testid="next-button"
        >
          Next: Review
        </button>
      </div>
    </div>
  );
}
